import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { useRemoveFromProjectsBulkDialogContent } from "@components/table/member-group-projects/bulk-actions/bulk-remove-from-projects-helper";
import { BaseMemberAndTeamProps } from "@custom-types/member-types";
import { useCompanyId } from "@context-providers/company-id-provider";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setBulkActionName } from "@store/table/table-slice";
import {
  selectedRowIdsSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useMemo } from "react";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";

interface Props extends BaseMemberAndTeamProps {
  /** Whether the dialog is open */
  isOpen: boolean;

  /** Callback when the dialog is closed */
  onClose(): void;

  /** The projects currently shown in the group (used as selectedEntities) */
  projects: FaroCoreApiTypes.ProjectSummary[];

  /** Callback after successful removal with the IDs of removed projects */
  onSuccessfulComplete(removedProjectIds: string[]): void;
}

/** Dialog for bulk removing a member/team from selected projects */
export function BulkRemoveFromProjectsDialog({
  isOpen,
  onClose,
  member,
  projects,
  onSuccessfulComplete,
}: Props): JSX.Element | null {
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);

  // Filter to only the projects that are selected via checkboxes
  const selectedProjects = useMemo(
    () => projects.filter((p) => selectedRowIds.includes(p.id)),
    [projects, selectedRowIds]
  );

  const { bulkDialogContent, successDialogContent } =
    useRemoveFromProjectsBulkDialogContent(member);

  // Close the main dialog when bulk action succeeds to show success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog && isOpen) {
      onClose();
    }
  }, [shouldShowSuccessDialog, isOpen, onClose]);

  // Set bulk action name when dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(setBulkActionName("removeMemberFromProject"));
    }
  }, [isOpen, dispatch]);

  async function onRemoveConfirmed(
    project: FaroCoreApiTypes.ProjectSummary
  ): Promise<void> {
    await dispatch(
      removeMemberFromProject({
        companyId,
        projectId: project.id,
        member,
      })
    ).unwrap();
  }

  /** Passes the removed project IDs to the parent callback */
  function handleSuccessfulComplete(): void {
    onSuccessfulComplete(selectedProjects.map((p) => p.id));
  }

  if (!isOpen) {
    return shouldShowSuccessDialog ? (
      <BulkSuccessDialog dialogContent={successDialogContent} />
    ) : null;
  }

  return (
    <FaroBulkDialog<FaroCoreApiTypes.ProjectSummary>
      dialogContent={bulkDialogContent}
      onClose={onClose}
      bulkActionCallback={onRemoveConfirmed}
      onSuccessfulComplete={handleSuccessfulComplete}
      trackingEvent={BulkActionEvents.bulkRemoveMemberFromProject}
      selectedEntities={selectedProjects}
      uniqueIdKey="id"
      tableDataType="projects"
    />
  );
}
